import { useFeature } from "@/hooks/useFeature";
import { useFeatures } from "@/hooks/useFeatures";
import { featureFlagsService } from "@/services";
import type { PropsWithChildren } from "react";
import * as React from "react";

export type FeatureGroup = {
  [featureName: string]: boolean | FeatureGroup;
};

export type FeatureFlags = string[] | FeatureGroup;

export const FeatureFlagsContext = React.createContext<FeatureGroup>({});

function transformFlags(features: FeatureFlags) {
  if (!Array.isArray(features)) return features;
  return Object.fromEntries(features.map((feature) => [feature, true]));
}

function mergeFeatures(a: FeatureGroup, b: FeatureGroup): FeatureGroup {
  return { ...a, ...b };
}

function FlagsProvider({
  features = {},
  children,
}: {
  features?: FeatureFlags;
  children: React.ReactNode;
}) {
  const currentFeatures = useFeatures();
  return (
    <FeatureFlagsContext.Provider
      value={mergeFeatures(
        transformFlags(currentFeatures),
        transformFlags(features)
      )}
    >
      {children}
    </FeatureFlagsContext.Provider>
  );
}

// Render Prop API
export function Feature({
  name,
  children,
  render = children,
}: {
  name: string;
  children?:
    | React.ReactNode
    | ((hasFeature: boolean | FeatureGroup) => JSX.Element);
  render?:
    | React.ReactNode
    | ((hasFeature: boolean | FeatureGroup) => JSX.Element);
}) {
  const hasFeature = useFeature(name);
  if (typeof render === "function") return render(hasFeature);
  if (!hasFeature) return null;
  return <React.Fragment>{render}</React.Fragment>;
}

export const FeatureFlagProvider = ({ children }: PropsWithChildren) => {
  return (
    <FlagsProvider features={featureFlagsService.config}>
      {children}
    </FlagsProvider>
  );
};
